import { useState, lazy, Suspense } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, X, Crown, Zap, Shield, Lock, Rocket } from "lucide-react";
import { usePlan } from "@/hooks/usePlan";
import type { PlanTier } from "@/lib/planGating";
import { PLAN_CONFIGS } from "@/lib/planConfig";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";

const StripeEmbeddedCheckout = lazy(() =>
  import("./StripeEmbeddedCheckout").then((m) => ({ default: m.StripeEmbeddedCheckout }))
);

const TIER_ICONS: Record<string, typeof Crown> = {
  free: Shield,
  pro: Zap,
  elite: Crown,
};

interface Props {
  open: boolean;
  onClose: () => void;
  requiredFeature?: string;
  trialExhausted?: boolean;
}

export default function UpgradeModal({ open, onClose, requiredFeature, trialExhausted }: Props) {
  const { plan: currentPlan, trial } = usePlan();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [checkoutPriceId, setCheckoutPriceId] = useState<string | null>(null);
  const [checkingSubscription, setCheckingSubscription] = useState(false);

  const isGuest = currentPlan === "guest";
  const showTrialBanner = trialExhausted || trial.trialExhausted;

  const handleSelectPlan = async (plan: typeof PLAN_CONFIGS[0]) => {
    if (isGuest) {
      try {
        sessionStorage.setItem("dos_upgrade_intent", JSON.stringify({ tier: plan.tier, priceId: plan.priceId }));
      } catch {}
      onClose();
      navigate("/auth");
      return;
    }

    if (plan.tier === currentPlan) return;

    if (plan.tier === "free") {
      handleManageSubscription();
      return;
    }

    if (!plan.priceId) return;

    if (user) {
      setCheckingSubscription(true);
      try {
        const { supabase } = await import("@/integrations/supabase/client");
        const { data: existingSub } = await supabase
          .from("subscriptions")
          .select("id, status, stripe_subscription_id")
          .eq("user_id", user.id)
          .in("status", ["active", "trialing"])
          .maybeSingle();

        if (existingSub) {
          setCheckingSubscription(false);
          handleManageSubscription();
          return;
        }
      } catch (e) {
        console.error("Subscription check error:", e);
      }
      setCheckingSubscription(false);
    }

    setCheckoutPriceId(plan.priceId);
  };

  const handleManageSubscription = async () => {
    if (!user) return;
    try {
      const { supabase } = await import("@/integrations/supabase/client");
      const { data } = await supabase.functions.invoke("create-portal-session", {
        body: {
          returnUrl: window.location.origin + "/profile",
          environment: import.meta.env.VITE_PAYMENTS_CLIENT_TOKEN?.startsWith("pk_test_") ? "sandbox" : "live",
        },
      });
      if (data?.url) {
        window.open(data.url, "_blank");
      }
    } catch (e) {
      console.error("Portal error:", e);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ type: "spring", damping: 25 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg mx-4 mb-4 sm:mb-0 rounded-2xl border border-border bg-card shadow-2xl overflow-hidden max-h-[85vh] overflow-y-auto"
          >
            {checkoutPriceId ? (
              <div className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-display font-bold text-foreground">Complete your upgrade</h2>
                  <button onClick={() => setCheckoutPriceId(null)} className="p-1.5 rounded-lg hover:bg-muted transition-colors">
                    <X className="w-4 h-4 text-muted-foreground" />
                  </button>
                </div>
                <Suspense fallback={<div className="py-12 text-center text-sm text-muted-foreground font-body">Loading checkout...</div>}>
                  <StripeEmbeddedCheckout
                    priceId={checkoutPriceId}
                    customerEmail={user?.email || undefined}
                    userId={user?.id || ""}
                    returnUrl={`${window.location.origin}/checkout/return?session_id={CHECKOUT_SESSION_ID}`}
                  />
                </Suspense>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                  <div>
                    <h2 className="text-lg font-display font-bold text-foreground">Choose your level of control</h2>
                    {requiredFeature && (
                      <p className="text-xs text-muted-foreground font-body mt-0.5 flex items-center gap-1">
                        <Lock className="w-3 h-3" />
                        {requiredFeature} requires a paid plan
                      </p>
                    )}
                  </div>
                  <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted transition-colors">
                    <X className="w-4 h-4 text-muted-foreground" />
                  </button>
                </div>

                {showTrialBanner && (
                  <div className="mx-5 mt-4 px-4 py-3 rounded-lg bg-primary/10 border border-primary/20">
                    <p className="text-xs font-body font-medium text-primary">
                      You've used all 5 Pro-level searches. Upgrade to keep the full engine.
                    </p>
                  </div>
                )}

                <div className="p-5 space-y-3">
                  {PLAN_CONFIGS.map((plan) => {
                    const isCurrent = currentPlan === plan.tier;
                    const Icon = TIER_ICONS[plan.tier] || Shield;

                    return (
                      <motion.div
                        key={plan.tier}
                        whileTap={{ scale: 0.98 }}
                        className={`rounded-xl border p-4 transition-all ${
                          plan.highlighted
                            ? "border-primary/30 bg-gradient-to-br from-primary/8 to-card"
                            : "border-border bg-card hover:border-primary/15"
                        } ${isCurrent ? "ring-1 ring-primary/40" : ""}`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Icon className={`w-4 h-4 ${plan.highlighted ? "text-primary" : "text-muted-foreground"}`} />
                            <span className="text-sm font-display font-semibold text-foreground">{plan.name}</span>
                            {isCurrent && (
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/15 text-primary font-body font-medium">
                                Current
                              </span>
                            )}
                          </div>
                          <div className="text-right">
                            <span className="text-xl font-display font-bold text-foreground">{plan.price}</span>
                            {plan.period && <span className="text-xs text-muted-foreground font-body">{plan.period}</span>}
                          </div>
                        </div>

                        <ul className="space-y-1.5 mb-4">
                          {plan.features.map((f) => (
                            <li key={f} className="flex items-center gap-2 text-xs font-body text-secondary-foreground">
                              <Check className="w-3 h-3 text-accent shrink-0" />
                              {f}
                            </li>
                          ))}
                        </ul>

                        <button
                          onClick={() => handleSelectPlan(plan)}
                          disabled={isCurrent || checkingSubscription}
                          className={`w-full py-2 rounded-lg font-body font-medium text-sm transition-all ${
                            isCurrent
                              ? "bg-muted text-muted-foreground cursor-default"
                              : checkingSubscription
                              ? "bg-muted text-muted-foreground cursor-wait"
                              : plan.highlighted
                              ? "bg-primary text-primary-foreground hover:opacity-90"
                              : "bg-secondary border border-border text-secondary-foreground hover:bg-primary/10 hover:border-primary/30"
                          }`}
                        >
                          {checkingSubscription
                            ? "Checking your plan…"
                            : isCurrent
                            ? "Current plan"
                            : isGuest
                            ? plan.tier === "free"
                              ? "Sign up to start free"
                              : `Sign up for ${plan.name}`
                            : plan.tier === "free"
                            ? "Manage subscription"
                            : `Upgrade to ${plan.name}`}
                        </button>
                      </motion.div>
                    );
                  })}
                </div>

                {currentPlan !== "free" && currentPlan !== "guest" && (
                  <div className="px-5 pb-3">
                    <button
                      onClick={handleManageSubscription}
                      className="w-full py-2 rounded-lg text-xs font-body text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors"
                    >
                      Manage billing
                    </button>
                  </div>
                )}

                <div className="px-5 pb-5">
                  <p className="text-[10px] text-muted-foreground font-body text-center">
                    Cancel anytime. Your plan stays active until the end of the billing period.
                  </p>
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
